import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Scale, 
  ChevronRight, RefreshCcw, Info, Eye, Star, Calculator,
  ShieldCheck, AlertCircle, Zap, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Trust Weight Multiplier (TWM) Admin Panel
 * Phase 1: Shadow Mode - Internal Admin Only
 * 
 * Combines ARS + BSS to evaluate offer credibility
 */
const TWMAdminPanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerDetails, setOfferDetails] = useState(null);
  const [activeOffers, setActiveOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch overview
      const overviewRes = await fetch(`${API_URL}/api/admin/twm/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const overviewData = await overviewRes.json();
      if (overviewData.success) {
        setOverview(overviewData.data);
      }

      // Fetch active offers
      const offersRes = await fetch(`${API_URL}/api/admin/twm/offers/active?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const offersData = await offersRes.json();
      if (offersData.success) {
        setActiveOffers(offersData.data.offers || []);
      }
    } catch (err) {
      setError('Error connecting to TWM service');
    }
    setLoading(false);
  };

  const fetchOfferDetails = async (offerId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/twm/offers/${offerId}/trust`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOfferDetails(data.data);
        setSelectedOffer(offerId);
      }
    } catch (err) {
      console.error('Error fetching offer details:', err);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'HIGH_TRUST': return 'bg-green-600/20 text-green-400 border-green-600/30';
      case 'STANDARD_TRUST': return 'bg-blue-600/20 text-blue-400 border-blue-600/30';
      case 'MODERATE_TRUST': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30';
      case 'LOW_TRUST': return 'bg-red-600/20 text-red-400 border-red-600/30';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'HIGH_TRUST': return 'High Trust';
      case 'STANDARD_TRUST': return 'Standard';
      case 'MODERATE_TRUST': return 'Moderate';
      case 'LOW_TRUST': return 'Low Trust';
      default: return 'Unscored';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'HIGH_TRUST': return <ShieldCheck className="w-4 h-4 text-green-400" />;
      case 'STANDARD_TRUST': return <Scale className="w-4 h-4 text-blue-400" />;
      case 'MODERATE_TRUST': return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'LOW_TRUST': return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMultiplierColor = (value) => {
    if (value >= 1.1) return 'text-green-400';
    if (value >= 1.0) return 'text-blue-400';
    if (value >= 0.9) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <RefreshCcw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading Trust Weight Analysis...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchData} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-primary flex items-center gap-2">
            <Scale className="w-6 h-6" />
            Trust Weight Multiplier
          </h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - Internal Only</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Phase Banner */}
      <Card className="glass-card border-cyan-600/30 bg-cyan-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Info className="w-5 h-5 text-cyan-400" />
          <div>
            <p className="text-cyan-400 font-medium">Shadow Mode Active</p>
            <p className="text-xs text-muted-foreground">
              TWM combines ARS + BSS to weight offers. Formula: Trust Weight = BSS × ARS × History × Timing
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Trust Distribution */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="glass-card border-green-600/30">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              <span className="text-3xl font-display text-green-400">{overview?.high_trust_offers || 0}</span>
            </div>
            <p className="text-xs text-muted-foreground">High Trust</p>
            <p className="text-xs text-green-400">≥1.0x</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-blue-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-blue-400">{overview?.standard_trust_offers || 0}</div>
            <p className="text-xs text-muted-foreground">Standard</p>
            <p className="text-xs text-blue-400">0.8-1.0x</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-yellow-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-yellow-400">{overview?.moderate_trust_offers || 0}</div>
            <p className="text-xs text-muted-foreground">Moderate</p>
            <p className="text-xs text-yellow-400">0.6-0.8x</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-red-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-red-400">{overview?.low_trust_offers || 0}</div>
            <p className="text-xs text-muted-foreground">Low Trust</p>
            <p className="text-xs text-red-400">{"<0.6x"}</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-gray-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-gray-400">{overview?.unscored_offers || 0}</div>
            <p className="text-xs text-muted-foreground">Unscored</p>
            <p className="text-xs text-gray-400">No Data</p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="glass-card border-white/10">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-display text-primary">{overview?.total_active_offers || 0}</div>
            <p className="text-xs text-muted-foreground">Active Offers</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-white/10">
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-display ${getMultiplierColor(overview?.avg_trust_weight || 1)}`}>
              {(overview?.avg_trust_weight || 1).toFixed(2)}x
            </div>
            <p className="text-xs text-muted-foreground">Avg Trust Weight</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-white/10">
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-display ${
              (overview?.avg_effective_value_adjustment || 0) >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {overview?.avg_effective_value_adjustment >= 0 ? '+' : ''}{overview?.avg_effective_value_adjustment?.toFixed(1) || 0}%
            </div>
            <p className="text-xs text-muted-foreground">Avg Value Adjustment</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Offers List */}
        <Card className="glass-card border-white/10 col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Trust-Weighted Offers
              <Badge variant="secondary" className="ml-auto">{activeOffers.length} Offers</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {activeOffers.map((offer) => (
                <div
                  key={offer.offer_id}
                  onClick={() => fetchOfferDetails(offer.offer_id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedOffer === offer.offer_id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`offer-row-${offer.offer_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary/30 flex items-center justify-center">
                        {getTierIcon(offer.trust_tier)}
                      </div>
                      <div>
                        <p className="font-medium">{formatCurrency(offer.offer_amount)}</p>
                        <p className="text-xs text-muted-foreground">Property: {offer.property_id?.slice(0, 8)}...</p>
                        <div className="flex gap-3 mt-1">
                          <span className={`text-xs ${getMultiplierColor(offer.trust_weight)}`}>
                            Weight: {offer.trust_weight?.toFixed(2)}x
                          </span>
                          <span className="text-xs text-primary">
                            Effective: {formatCurrency(offer.effective_value)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-xs">
                        <p className="text-muted-foreground">BSS: <span className="text-purple-400">{offer.bss_tier || 'N/A'}</span></p>
                        <p className="text-muted-foreground">ARS: <span className="text-blue-400">{offer.ars_tier || 'N/A'}</span></p>
                      </div>
                      <Badge className={getTierColor(offer.trust_tier)}>
                        {getTierLabel(offer.trust_tier)}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
              
              {activeOffers.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Scale className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No active offers to analyze</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Offer Details Panel */}
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Trust Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {offerDetails ? (
              <div className="space-y-4">
                {/* Trust Weight Display */}
                <div className="text-center p-4 rounded-lg bg-secondary/20">
                  <div className={`text-5xl font-display mb-2 ${getMultiplierColor(offerDetails.trust_weight)}`}>
                    {offerDetails.trust_weight?.toFixed(2)}x
                  </div>
                  <Badge className={getTierColor(offerDetails.trust_tier)}>
                    {getTierLabel(offerDetails.trust_tier)}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-2">
                    Confidence: {offerDetails.confidence}
                  </p>
                </div>

                {/* Value Comparison */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded bg-secondary/10 text-center">
                    <div className="text-sm font-display text-foreground">
                      {formatCurrency(offerDetails.offer_amount)}
                    </div>
                    <p className="text-xs text-muted-foreground">Offer Amount</p>
                  </div>
                  <div className="p-3 rounded bg-cyan-600/10 text-center">
                    <div className="text-sm font-display text-cyan-400">
                      {formatCurrency(offerDetails.effective_value)}
                    </div>
                    <p className="text-xs text-muted-foreground">Effective Value</p>
                  </div>
                </div>

                {/* Component Breakdown */}
                <div className="space-y-3">
                  <p className="text-sm font-medium">Component Multipliers</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">BSS (Buyer)</span>
                      <span className={`text-sm font-medium ${getMultiplierColor(offerDetails.bss_multiplier)}`}>
                        {offerDetails.bss_multiplier?.toFixed(2)}x
                      </span>
                    </div>
                    <Progress value={offerDetails.bss_multiplier * 50} className="h-2" />
                    <p className="text-xs text-purple-400">{offerDetails.buyer_bss_tier || 'Unscored'}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ARS (Agent)</span>
                      <span className={`text-sm font-medium ${getMultiplierColor(offerDetails.ars_multiplier)}`}>
                        {offerDetails.ars_multiplier?.toFixed(2)}x
                      </span>
                    </div>
                    <Progress value={offerDetails.ars_multiplier * 50} className="h-2" />
                    <p className="text-xs text-blue-400">{offerDetails.agent_ars_tier || 'No Agent'}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">History Factor</span>
                      <span className={`text-sm font-medium ${getMultiplierColor(offerDetails.history_factor)}`}>
                        {offerDetails.history_factor?.toFixed(2)}x
                      </span>
                    </div>
                    <Progress value={offerDetails.history_factor * 50} className="h-2" />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Timing Factor</span>
                      <span className={`text-sm font-medium ${getMultiplierColor(offerDetails.timing_factor)}`}>
                        {offerDetails.timing_factor?.toFixed(2)}x
                      </span>
                    </div>
                    <Progress value={offerDetails.timing_factor * 50} className="h-2" />
                    {offerDetails.days_since_viewing !== null && (
                      <p className="text-xs text-muted-foreground">
                        {offerDetails.days_since_viewing} days since viewing
                      </p>
                    )}
                  </div>
                </div>

                {/* Trust Signals */}
                {offerDetails.trust_signals?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-green-400">Trust Signals</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {offerDetails.trust_signals.map((signal, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <ShieldCheck className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                          {signal}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Caution Signals */}
                {offerDetails.caution_signals?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-orange-400">Caution Signals</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {offerDetails.caution_signals.map((signal, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <AlertCircle className="w-3 h-3 text-orange-400 mt-0.5 flex-shrink-0" />
                          {signal}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendation */}
                <div className="p-3 rounded bg-secondary/10 text-sm">
                  <p className="text-xs font-medium text-primary mb-1">Recommendation</p>
                  <p className="text-muted-foreground">{offerDetails.recommendation}</p>
                </div>

                {/* Metadata */}
                <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-white/10">
                  <p>Offer vs Asking: {offerDetails.offer_vs_asking_pct?.toFixed(1)}%</p>
                  <p>First Offer: {offerDetails.is_first_offer ? 'Yes' : 'No'}</p>
                  <p>Calculated: {new Date(offerDetails.calculated_at).toLocaleString()}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Calculator className="w-8 h-8 mx-auto mb-4 opacity-50" />
                <p>Select an offer to view trust analysis</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Formula Explanation */}
      <Card className="glass-card border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium">Trust Weight Formula</span>
          </div>
          <div className="bg-secondary/20 rounded p-3 font-mono text-sm text-center">
            <span className="text-cyan-400">Trust Weight</span> = 
            <span className="text-purple-400"> BSS</span> × 
            <span className="text-blue-400"> ARS</span> × 
            <span className="text-yellow-400"> History</span> × 
            <span className="text-green-400"> Timing</span>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-3 text-xs text-muted-foreground">
            <div><span className="text-purple-400">BSS:</span> Buyer credibility (0.5-1.2)</div>
            <div><span className="text-blue-400">ARS:</span> Agent reliability (0.65-1.15)</div>
            <div><span className="text-yellow-400">History:</span> Past behavior (0.75-1.05)</div>
            <div><span className="text-green-400">Timing:</span> Decision speed (0.95-1.05)</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TWMAdminPanel;
